"use client";

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useRouter } from "next/navigation";
import {
  Button,
  LoadingSpinner,
  Sheet,
  TabSelect,
  useCopyToClipboard,
  useMediaQuery,
} from "@panda0/panda0-ui";
import { useCompletion } from "ai/react";
import { Loader2, X } from "lucide-react";
import Papa from "papaparse";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import useWorkspace from "@/lib/swr/use-workspace";
import EmailSharingTab from "./share/email-sharing";
import EmbedSharingTab from "./share/embed-sharing";
import LinkSharingTab from "./share/link-sharing";
import TemplateEditor from "./share/template-editor";
import { Candidate, EmailTemplate, JobModalContext } from "./types";

// Example of email templates
const DEFAULT_TEMPLATES: EmailTemplate[] = [
  {
    id: "1",
    name: "Technical Quiz Invitation",
    subject: "Your Technical Quiz for {jobTitle} Position",
    body: `Dear {firstName},

Thank you for your interest in the {jobTitle} position at {companyName}. As part of our selection process, we'd like to invite you to complete a technical quiz.

This quiz is designed to evaluate your skills in areas relevant to the role. It should take approximately 5 minutes to complete.

Please click the link below to start your PandaQuiz:
{pandaQuizLink}

The PandaQuiz will be available until {deadline}. If you have any questions or need assistance, please don't hesitate to reach out.

Best regards,
{recruiterName}
{companyName}`,
  },
  {
    id: "2",
    name: "Quick Technical Quiz",
    subject: "Quick Technical Quiz Required - {jobTitle}",
    body: `Hello {firstName},

As part of your application for the {jobTitle} role, we've prepared a short technical quiz to assess your skills.

Please complete the screening at your earliest convenience using this link:
{pandaQuizLink}

This should only take under 5 minutes to complete.

Thank you for your time!

Regards,
{recruiterName}
{companyName}`,
  },
];

function JobShare() {
  const { isMobile } = useMediaQuery();
  const router = useRouter();
  const { stateMachine, savedJobId, jobData, saveJobData, workspaceId, workspacePlan } =
    useContext(JobModalContext);
  const { id: wsId, logo, companyName, companyUrl } = useWorkspace();
  const [_, copyToClipboard] = useCopyToClipboard();

  // State management
  // const [activeTab, setActiveTab] = useState<"link" | "email">("email");

  const [generatingEmail, setGeneratingEmail] = useState(false);
  const [selectedTemplate, setSelectedTemplate] =
    useState<EmailTemplate | null>(DEFAULT_TEMPLATES[0]);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [shortLink, setShortLink] = useState("");
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [loadingJobDetails, setLoadingJobDetails] = useState(true);
  const [jobDetails, setJobDetails] = useState<{
    title: string;
    company: string;
    deadline: string;
    recruiterName: string;
    shortLinkKey: string;
  }>({
    title: "",
    company: "",
    deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    recruiterName: "",
    shortLinkKey: "",
  });

  const emailForm = useForm({
    defaultValues: useMemo(
      () => ({
        subject: "",
        body: "",
        signature: "",
        useSignature: true,
      }),
      []
    ),
  });

  // Load job details when jobId changes
  useEffect(() => {
    const actualJobId = savedJobId;
    if (actualJobId) {
      setLoadingJobDetails(true);

      // Check if we already have the job data in context
      if (jobData) {
        setJobDetails({
          title: jobData.basic?.title || "Job Position",
          company: companyName || "",
          deadline: jobData.basic?.campaignEnd
            ? new Date(jobData.basic?.campaignEnd).toISOString().split("T")[0]
            : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
              .toISOString()
              .split("T")[0],
          recruiterName: "",
          shortLinkKey: jobData.basic?.key || `j-${actualJobId}`,
        });
        setShortLink(
          `${window.location.origin}/${jobData.basic?.key || `j-${actualJobId}`}`
        );

        // Load candidates if available
        // if (jobData.candidates?.length > 0) {
        //   setCandidates(jobData.candidates);
        // }

        setLoadingJobDetails(false);
        return;
      }

      // Fetch job details if not available in context
      fetch(`/api/v1/jobs/${actualJobId}?workspaceId=${wsId}`)
        .then((res) => res.json())
        .then((data) => {
          setJobDetails({
            title: data.title || "Job Position",
            company: data.company || "",
            deadline: data.campaignEnd
              ? new Date(data.campaignEnd).toISOString().split("T")[0]
              : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
                .toISOString()
                .split("T")[0],
            recruiterName: data.recruiterName || "",
            shortLinkKey: data.key || `j-${actualJobId}`,
          });
          setShortLink(
            `${window.location.origin}/${data.key || `j-${actualJobId}`}`
          );

          // Load candidates if available
          if (data.candidates?.length > 0) {
            setCandidates(data.candidates);
          }

          setLoadingJobDetails(false);
        })
        .catch((error) => {
          console.error("Error loading job details:", error);
          // Fallback to mock data
          setJobDetails({
            title: "Senior Software Engineer",
            company: "",
            deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
              .toISOString()
              .split("T")[0],
            recruiterName: "",
            shortLinkKey: `j-${actualJobId}`,
          });
          setShortLink(`${window.location.origin}/j-${actualJobId}`);
          setLoadingJobDetails(false);
        });
    }
  }, [savedJobId, jobData]);

  useEffect(() => {
    if (selectedTemplate) {
      let subject = selectedTemplate.subject;
      let body = selectedTemplate.body;

      // Replace placeholders with job details
      subject = subject
        .replace("{jobTitle}", jobDetails.title)
        .replace("{companyName}", jobDetails.company);

      body = body
        .replace("{jobTitle}", jobDetails.title)
        .replace("{companyName}", jobDetails.company)
        .replace("{pandaQuizLink}", shortLink)
        .replace(
          "{deadline}",
          new Date(jobDetails.deadline).toLocaleDateString()
        )
        .replace("{recruiterName}", jobDetails.recruiterName);

      emailForm.setValue("subject", subject);
      emailForm.setValue("body", body);
    }
  }, [selectedTemplate, jobDetails, shortLink]);

  // Save sharing details to server
  const saveShareDetails = async () => {
    if (!savedJobId) return;

    stateMachine.trackEvent("job_share_save_details", {
      candidate_count: candidates.length,
      has_template: Boolean(
        emailForm.getValues().subject && emailForm.getValues().body
      ),
      template_length: (emailForm.getValues().body || "").length,
    });

    try {
      const shareData = {
        candidates,
        emailTemplate: {
          subject: emailForm.getValues().subject,
          body: emailForm.getValues().body,
          signature: emailForm.getValues().signature,
          useSignature: emailForm.getValues().useSignature,
        },
      };
      await stateMachine.saveStep("share", true);
      toast.success("Share details saved");
    } catch (error) {
      console.error("Error saving share details:", error);
      // Error handled by saveJobData
    }
  };

  const { complete: generateEmail, isLoading: isGeneratingEmail } =
    useCompletion({
      api: `/api/v1/ai/completion?workspaceId=${wsId}`,
      onFinish: (_, completion) => {
        try {
          const emailContent = completion.trim();
          emailForm.setValue("body", emailContent);
          setGeneratingEmail(false);
          toast.success("Email template generated successfully!");

          // Save the generated template
          saveShareDetails();
        } catch (error) {
          console.error("Error parsing generated email:", error);
          toast.error("Failed to generate email template");
          setGeneratingEmail(false);
        }
      },
    });

  const handleGenerateEmail = async () => {
    stateMachine.trackEvent("job_share_generate_email", {
      has_job_details: Boolean(jobDetails?.title),
      job_title: jobDetails?.title,
    });
    setGeneratingEmail(true);

    const prompt = `Create a professional email template for inviting candidates to take a technical quiz for a ${jobDetails.title} position at ${jobDetails.company || "our company"}.

    The email should:
    1. Be friendly but professional
    2. Briefly explain the purpose of the quiz
    3. Include a placeholder for the quiz link: ${jobDetails.shortLinkKey}
    4. Mention the deadline: ${new Date(jobDetails.deadline).toLocaleDateString()}
    5. Include a sign-off from ${jobDetails.recruiterName || "the hiring team"}

    Only return the email content, no additional text.`;

    await generateEmail(prompt);
  };

  // Candidate management handlers
  const handleCSVImport = useCallback((file: File) => {
    Papa.parse(file, {
      header: true,
      complete: (results) => {
        const parsedCandidates = results.data
          .filter((row: any) => row.email && (row.firstName || row.lastName))
          .map((row: any, index) => ({
            id: `imported-${index}`,
            firstName: row.firstName || row.first_name || "",
            lastName: row.lastName || row.last_name || "",
            email: row.email || "",
            company: row.company || row.organization || "",
            custom1: row.custom1 || "",
            custom2: row.custom2 || "",
          }));

        if (parsedCandidates.length === 0) {
          stateMachine.trackEvent("job_share_csv_import_failed", {
            reason: "no_valid_candidates",
            row_count: results.data.length,
          });

          toast.error("No valid candidates found in the CSV file");
          return;
        }

        stateMachine.trackEvent("job_share_csv_import_success", {
          candidate_count: parsedCandidates.length,
          total_rows: results.data.length,
        });

        setCandidates(parsedCandidates);
        toast.success(
          `Successfully imported ${parsedCandidates.length} candidates`
        );

        // Save imported candidates
        saveShareDetails();
      },
      error: (error) => {
        stateMachine.trackEvent("job_share_csv_import_error", {
          error_message: error.message,
        });
        console.error("Error parsing CSV:", error);
        toast.error("Failed to parse CSV file");
      },
    });
  }, []);

  const addCandidate = () => {
    const newCandidates = [
      ...candidates,
      {
        id: `new-${Date.now()}`,
        firstName: "",
        lastName: "",
        email: "",
        company: "",
      },
    ];

    setCandidates(newCandidates);

    // Don't save immediately - wait for user to fill in details
  };

  const removeCandidate = (id: string) => {
    const updatedCandidates = candidates.filter((c) => c.id !== id);
    setCandidates(updatedCandidates);

    // Save after removing
    setTimeout(() => saveShareDetails(), 100);
  };

  const updateCandidate = (
    id: string,
    field: keyof Candidate,
    value: string
  ) => {
    const updatedCandidates = candidates.map((c) =>
      c.id === id ? { ...c, [field]: value } : c
    );

    setCandidates(updatedCandidates);

    // Use debouncing to avoid too many saves
    if (debounceSaveTimeout) clearTimeout(debounceSaveTimeout);
    debounceSaveTimeout = setTimeout(() => saveShareDetails(), 1000);
  };

  let debounceSaveTimeout: NodeJS.Timeout | null = null;

  const sendEmails = async () => {
    const { subject, body, signature, useSignature } = emailForm.getValues();

    if (!subject || !body) {
      toast.error("Please provide both subject and body for the email");
      return;
    }

    if (candidates.length === 0) {
      toast.error("Please add at least one candidate");
      return;
    }

    // Validate emails
    const invalidEmails = candidates.filter(
      (c) => !c.email || !c.email.includes("@")
    );
    if (invalidEmails.length > 0) {
      toast.error(
        `${invalidEmails.length} candidates have invalid email addresses`
      );
      return;
    }

    stateMachine.trackEvent("job_share_send_emails", {
      candidate_count: candidates.length,
      subject_length: subject.length,
      body_length: body.length,
      includes_signature: useSignature,
    });

    try {
      toast.loading(`Sending emails to ${candidates.length} candidates...`);

      // Call API to send emails
      const res = await fetch(
        `/api/v1/jobs/${savedJobId}/send-emails?workspaceId=${wsId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            candidates,
            emailTemplate: {
              subject,
              body,
              signature,
              useSignature,
            },
          }),
        }
      );

      if (!res.ok) {
        const errorData = await res.json();
        stateMachine.trackEvent("job_share_send_emails_api_error", {
          status: res.status,
          error_message: errorData.error || "Unknown error",
        });

        throw new Error(errorData.error || "Failed to send emails");
      }

      const result = await res.json();
      toast.dismiss();

      if (result.results.failed && result.results.failed.length > 0) {
        stateMachine.trackEvent("job_share_send_emails_partial_success", {
          sent_count: result.results.sent.length,
          failed_count: result.results.failed.length,
          failure_reasons: result.results.failed
            .map((f) => f.reason || "unknown")
            .slice(0, 5),
        });

        toast.error(
          `Failed to send ${result.results.failed.length} emails. ${result.results.sent.length} emails were sent successfully.`
        );
      } else {
        stateMachine.trackEvent("job_share_send_emails_success", {
          sent_count: result.results.sent.length,
        });

        toast.success(
          `Successfully sent emails to ${result.results.sent.length} candidates!`
        );
      }

      // Save share details after sending
      await saveShareDetails();
    } catch (error) {
      stateMachine.trackEvent("job_share_send_emails_error", {
        error_message: error instanceof Error ? error.message : "Unknown error",
      });
      console.error("Error sending emails:", error);
      toast.dismiss();
      toast.error(
        error instanceof Error ? error.message : "Failed to send emails"
      );
    }
  };

  const copyLink = async () => {
    stateMachine.trackEvent("job_share_copy_link", {
      link: shortLink,
    });
    try {
      await copyToClipboard(shortLink);
      toast.success("Link copied to clipboard!");
    } catch (error) {
      toast.error("Failed to copy link");
    }
  };

  const [activeTab, setActiveTab] = useState("sharelink");
  const tabs = [
    { id: "sharelink", label: "Share Link" },
    { id: "email", label: "Email Candidates" },
    { id: "embed", label: "Embed" },
  ];

  return (
    <div className="mx-auto w-full">
      {loadingJobDetails ? (
        <div className="flex h-[400px] items-center justify-center">
          <LoadingSpinner/>
        </div>
      ) : (
        <div>
          <div className="p-6 space-y-8">
            <TabSelect
              selected={activeTab}
              onSelect={setActiveTab}
              className="w-full"
              options={tabs}
            ></TabSelect>
            {activeTab === "sharelink" && (
              <LinkSharingTab
                shortLink={shortLink}
                jobDetails={jobDetails}
                copyLink={copyLink}
              />
            )}
            {activeTab === "email" && (
              <EmailSharingTab
                emailForm={emailForm}
                selectedTemplate={selectedTemplate}
                setSelectedTemplate={setSelectedTemplate}
                templates={DEFAULT_TEMPLATES}
                candidates={candidates}
                setCandidates={setCandidates}
                addCandidate={addCandidate}
                removeCandidate={removeCandidate}
                updateCandidate={updateCandidate}
                handleCSVImport={handleCSVImport}
                handleGenerateEmail={handleGenerateEmail}
                isGeneratingEmail={isGeneratingEmail}
                shortLink={shortLink}
                sendEmails={sendEmails}
                setShowTemplateEditor={setShowTemplateEditor}
              />
            )}
            {activeTab === "embed" && (
              <EmbedSharingTab key={jobData?.basic?.key} />
            )}
          </div>
        </div>
      )}

      {/* Template Editor Sheet */}
      <Sheet open={showTemplateEditor} onOpenChange={setShowTemplateEditor}>
        <div className="flex justify-between items-center mb-6 p-4">
          <Sheet.Title className="text-xl font-semibold">
            Email Template Editor
          </Sheet.Title>
          <Sheet.Close asChild>
            <Button
              variant="outline"
              icon={<X className="size-5" />}
              className="h-auto w-fit p-1"
            />
          </Sheet.Close>
        </div>
        <TemplateEditor
          template={selectedTemplate}
          onSave={(template) => {
            if (selectedTemplate) {
              const updatedTemplates = DEFAULT_TEMPLATES.map((t) =>
                t.id === template.id ? template : t
              );
              setSelectedTemplate(template);
            } else {
              const newTemplate = {
                ...template,
                id: `template-${Date.now()}`,
              };
              setSelectedTemplate(newTemplate);
            }
            setShowTemplateEditor(false);
          }}
          onCancel={() => setShowTemplateEditor(false)}
        />
      </Sheet>
    </div>
  );
}

export default React.memo(JobShare);
